<template>
    <div class="py-12" data-cy="kiosk-users-index">
        <DataTable
            data-cy="kiosk-users-table"
            title="Users"
            searchable
            invoice-input
            :headers="headers"
            :items="users"
            :filters="filters"
        >
            <template #item-profile_photo_path="{ data }">
                <div class="flex justify-center">
                    <img
                        v-if="data"
                        class="object-cover w-10 h-10 rounded-full"
                        :src="data"
                    />
                    <img
                        v-else
                        class="object-cover w-10 h-10 rounded-full"
                        src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/ac/No_image_available.svg/600px-No_image_available.svg.png"
                    />
                </div>
            </template>

            <template #item-subscriptions="{ row }">
                <div
                    class="w-[300px] text-left text-sm font-medium text-gray-500"
                >
                    {{
                        [...new Set(
                            row.subscriptions
                                .filter((sub: ISubscription) => sub.is_active === true)
                                .map((sub: ISubscription) => sub.subscription_plan_uuid)
                        )].join(', ')
                    }}
                </div>
            </template>

            <template #item-custom="{ row }">
                <div class="flex justify-center w-full">
                    <AtomicButton
                        color="white"
                        size="sm"
                        data-cy="kiosk-user-expand"
                        @click="handleShowUser(row)"
                    >
                        <template #icon>
                            <MagnifyingGlassIcon class="w-6 h-6" />
                        </template>
                    </AtomicButton>
                </div>
            </template>
        </DataTable>
    </div>
</template>

<script lang="ts" setup>
import DataTable from '@/components/Atomic/DataTable/DataTable.vue';
import IPaginationData from '@/types/Atomic/IPaginationData';
import AtomicButton from '@atomic/Buttons/AtomicButton.vue';
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import { route } from 'ziggy-js';
import IUser from '@/types/Members/IUser';
import IDataTableHeader from '@/components/Atomic/DataTable/types/IDataTableHeader';
import { IDataTableFilter } from '@/components/Atomic/DataTable/types/IDataTableFilter';
import { useUrlHistory } from '@/composables/useUrlHistory';

import { usePage } from '@inertiajs/vue3';
import { sub } from 'date-fns';
import ISubscription from '@/types/Subscription/ISubscription';

defineProps<{
    users: IPaginationData<IUser>;
}>();

onMounted(() => {
    saveHistoryOfResults(usePage().url);
});

const { saveHistoryOfResults } = useUrlHistory();

watch(
    () => usePage().url, // Watch the URL in the page object
    (newUrl) => {
        saveHistoryOfResults(newUrl);
    }
);

const headers: IDataTableHeader[] = [
    { text: '', id: 'profile_photo_path' },
    {
        text: 'ID',
        id: 'id',
        sortable: true,
        sortableId: 'id',
    },
    {
        text: 'Name',
        id: 'name',
        sortable: true,
        sortableId: 'name',
    },
    {
        text: 'Email',
        id: 'email',
        sortable: true,
        sortableId: 'email',
    },
    { text: 'Subscriptions', id: 'subscriptions' },
    { text: '', id: 'custom' },
];

const filters: IDataTableFilter[] = [
    {
        column: 'type',
        label: 'User Type',
        values: [
            {
                label: 'Regular users',
                value: 'regular',
            },
            {
                label: 'Admins',
                value: 'admin',
            },
        ],
    },
    {
        column: 'trial',
        label: 'Trial',
        values: [
            {
                label: 'PRO Trial',
                value: 'pro_trial_ends_at',
            },
            {
                label: 'Plugin Trial',
                value: 'plugin_trial_ends_at',
            },
        ],
    },
];

const handleShowUser = function (user: IUser) {
    router.get(
        route('kiosk.users.activity.index', {
            user: user.id,
        })
    );
};
</script>

<style></style>
